import { createFileRoute } from '@tanstack/react-router';
import { productIdPathParams } from '@/lib/product-url-codec';
import { z } from 'zod';
import { CheckCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { AgentRunCard } from '@/components/AgentRunCard';
import { AgentPollingPanel } from '@/components/AgentPollingPanel';
import { formatDuration, formatServerDateShort, parseServerDate } from '@/lib/dates';
import { useProducts } from '@/features/product/hooks';
import {
  useClinicalReportDispatchDetail,
  useClinicalReportRun,
} from '@/features/clinical-report/hooks';
import type { ClinicalReportRunDetail } from '@/features/clinical-report/api';
import { ClinicalReportResultRenderer } from '@/features/clinical-report/components/ClinicalReportResultRenderer';
import { ClinicalReportDispatchForm } from '@/features/clinical-report/components/ClinicalReportDispatchForm';
import { ReportStaleBanner } from '@/features/clinical-report/components/ReportStaleBanner';
import { PageHero } from '@/components/layout/PageHero';
import { ScrollToTopButton } from '@/components/ScrollToTopButton';
import { DispatchTabsShell } from '@/features/agent-dispatch/DispatchTabsShell';
import { QueuedBanner } from '@/features/agent-dispatch/QueuedBanner';
import { FailedBanner as DispatchFailedBanner } from '@/features/agent-dispatch/FailedBanner';
import { countryLabel } from '@/features/agent-dispatch/countryLabel';
import type { DispatchData } from '@/features/agent-dispatch/types';
import { RunLoadingSkeleton } from '@/components/RunLoadingSkeleton';

/**
 * Clinical Study Report result page.
 *
 * URL: /clinical-reports/:productId?report_run_id=<n>
 */

const searchSchema = z.object({
  report_run_id: z.coerce.number().int().positive().optional().catch(undefined),
});

const REPORT_WORKFLOW_STEPS = [
  'Read product specifications',
  'Review clinical evidence',
  'Apply ICH E3 structure',
  'Compose efficacy findings',
  'Compose safety findings',
  'Draft statistical analysis',
  'Finalize CSR',
] as const;

const REPORT_STEP_SECONDS = 14;

export const Route = createFileRoute('/clinical-reports_/$productId')({
  // Opaque token in the address bar, never the global product id.
  params: productIdPathParams,
  validateSearch: searchSchema,
  component: ClinicalReportResultPage,
});

function ClinicalReportResultPage() {
  const { productId } = Route.useParams();
  const { report_run_id: runId } = Route.useSearch();
  const products = useProducts();
  const product = (products.data ?? []).find((p) => String(p.id) === productId) ?? null;
  const { data: run, isLoading, isError } = useClinicalReportRun(runId ?? null);

  return (
    <>
      <PageHero
        title="Clinical Study Report"
        subtitle="A regulator-aligned Clinical Study Report (ICH E3 structure) for this product — efficacy and safety findings with statistical analysis — grounded in your product's clinical evidence via the REGARA AI agent."
      />

      <main className="max-w-[1280px] mx-auto px-4 sm:px-6 lg:px-10 pt-7 pb-20">
        {runId === undefined && (
          <>
            {products.isLoading && (
              <RunLoadingSkeleton label="Loading product" />
            )}
            {!products.isLoading && !product && (
              <NotFoundCard productId={productId} />
            )}
            {product && <ClinicalReportDispatchForm product={product} />}
          </>
        )}

        {runId !== undefined && isLoading && (
          <RunLoadingSkeleton />
        )}
        {runId !== undefined && isError && (
          <ErrorCard message="Could not load the run. Please refresh." />
        )}
        {runId !== undefined && run && (
          run.dispatch_group_id ? (
            // ``initialRunId`` opens the tab shell ON the run the URL targeted, not
            // the first sibling (same fix as the sibling result pages).
            <DispatchView groupId={run.dispatch_group_id} initialRunId={run.id} />
          ) : (
            <>
              <AgentRunCard run={run} agentLabel="CSR run" />
              <RunStateBody run={run} />
              {run.status === 'completed' && <ResultDisclaimer />}
            </>
          )
        )}
      </main>
      <ScrollToTopButton />
    </>
  );
}

/** Multi-country view. Polls every sibling run in the dispatch group
 *  and renders one tab per country. When the dispatch has a single
 *  child (today's single-country case), the shell auto-collapses to
 *  the legacy inline view. Mirrors the clinical-protocol-side
 *  ``DispatchView`` exactly. */
function DispatchView({
  groupId,
  initialRunId,
}: {
  groupId: string;
  initialRunId?: number | null;
}) {
  const { data, isLoading, isError } = useClinicalReportDispatchDetail(groupId);
  const shellData: DispatchData | undefined = data
    ? { dispatch_group_id: data.dispatch_group_id, runs: data.runs }
    : undefined;
  return (
    <DispatchTabsShell
      data={shellData}
      isLoading={isLoading}
      isError={isError}
      initialRunId={initialRunId}
      renderRunMeta={(runId) => <ReportRunMetaCard runId={runId} />}
      renderSingleRun={(runId) => <ReportSingleRun runId={runId} />}
      renderRunBody={(runId, country) => (
        <ReportRunBody runId={runId} country={country} />
      )}
    />
  );
}

function ReportRunMetaCard({ runId }: { runId: number }) {
  const { data: run } = useClinicalReportRun(runId);
  if (!run) return null;
  return <AgentRunCard run={run} agentLabel="CSR run" />;
}

function ReportSingleRun({ runId }: { runId: number }) {
  const { data: run, isLoading, isError } = useClinicalReportRun(runId);
  if (isLoading) {
    return <RunLoadingSkeleton />;
  }
  if (isError || !run) {
    return <ErrorCard message="Could not load the run. Please refresh." />;
  }
  return (
    <>
      <AgentRunCard run={run} agentLabel="CSR run" />
      <RunStateBody run={run} />
      {run.status === 'completed' && <ResultDisclaimer />}
    </>
  );
}

function ReportRunBody({
  runId,
  country,
}: {
  runId: number;
  country: string | null;
}) {
  const { data: run, isLoading, isError } = useClinicalReportRun(runId);
  if (isLoading) {
    return <RunLoadingSkeleton />;
  }
  if (isError || !run) {
    return <ErrorCard message="Could not load the run. Please refresh." />;
  }
  return (
    <>
      <RunStateBody run={run} country={country} />
      {run.status === 'completed' && <ResultDisclaimer />}
    </>
  );
}

function NotFoundCard({ productId }: { productId: string }) {
  return (
    <div className="bg-white border border-line rounded-2xl p-12 text-center shadow-card">
      <p className="text-navy text-lg font-semibold m-0 mb-2">Product not found</p>
      <p className="text-muted text-sm m-0 mb-6">
        Product <code>#{productId}</code> doesn't exist or you don't have access to it.
      </p>
      <Button variant="outline" asChild>
        <a href="/clinical-reports">← Back to product list</a>
      </Button>
    </div>
  );
}

function RunStateBody({ run, country }: { run: ClinicalReportRunDetail; country?: string | null }) {
  if (run.status === 'queued') {
    return <QueuedBanner country={country} />;
  }
  if (run.status === 'running') {
    return (
      <AgentPollingPanel
        startedAt={run.started_at}
        workflowSteps={REPORT_WORKFLOW_STEPS}
        stepSeconds={REPORT_STEP_SECONDS}
        title={
          country ? (
            <>
              CSR Drafting in Progress
              <span className="block mt-1.5 font-sans text-electric text-[22px] font-bold tracking-tight">
                {countryLabel(country)}
              </span>
            </>
          ) : (
            'CSR Drafting in Progress'
          )
        }
        subtitle="Drafting the Clinical Study Report via the REGARA AI agent. You can safely leave this page — the run continues on the server, and this link picks it back up."
      />
    );
  }

  if (run.status === 'failed') {
    return (
      <DispatchFailedBanner
        country={country}
        message={friendlyErrorMessage(run.error_message)}
      />
    );
  }

  return <CompletedSection run={run} country={country} />;
}

function CompletedSection({
  run,
  country,
}: {
  run: ClinicalReportRunDetail;
  country?: string | null;
}) {
  const startedAt = parseServerDate(run.started_at);
  const completedAt = parseServerDate(run.completed_at);
  const durationSec =
    startedAt && completedAt
      ? Math.round((completedAt.getTime() - startedAt.getTime()) / 1000)
      : null;

  const response = run.report_response;

  return (
    <>
      {/* Run's own country first — the collapsed single-run view passes no
          tab country, and the banner scopes per market. */}
      <ReportStaleBanner
        productId={run.product_id}
        runId={run.id}
        runCompletedAt={run.completed_at}
        country={run.country ?? country ?? null}
      />
      <div className="mb-[22px] py-[18px] px-[22px] bg-state-success-bg border border-state-success-border rounded-xl">
        <div className="flex items-start gap-3">
          <CheckCircle className="size-6 text-state-success-accent shrink-0 mt-0.5" />
          <div className="flex-1 min-w-0">
            <h3 className="m-0 mb-1.5 text-[17px] font-bold text-state-success-text">
              CSR Complete
              {country && (
                <span className="block mt-1 text-navy text-[24px] font-bold tracking-tight">
                  {countryLabel(country)}
                </span>
              )}
            </h3>
            <p className="text-sm leading-[1.55] text-state-success-text/90 m-0">
              Completed {formatServerDateShort(run.completed_at)}
              {durationSec !== null && ` · ${formatDuration(durationSec)}`}
            </p>
          </div>
        </div>
      </div>

      {response ? (
        <ClinicalReportResultRenderer response={response} runId={run.id} />
      ) : (
        <div className="bg-state-error-bg border border-state-error-border rounded-2xl p-6">
          <div className="font-semibold text-state-error-text text-lg">
            Could not parse the report response
          </div>
          <p className="text-sm text-state-error-text mt-1">
            The run completed but the report payload was not delivered. Try generating a
            new report.
          </p>
        </div>
      )}
    </>
  );
}

function ErrorCard({ message }: { message: string }) {
  return (
    <div className="bg-state-error-bg border border-state-error-border rounded-2xl p-8 text-state-error-text">
      {message}
    </div>
  );
}

function ResultDisclaimer() {
  return (
    <footer className="mt-7 py-[18px] px-[22px] bg-state-warning-bg-soft border border-state-warning-border rounded-xl text-[#713f12] text-base leading-[1.6]">
      <strong className="block text-xs font-bold uppercase tracking-[0.22em] text-state-warning-text mb-1.5">
        Disclaimer.
      </strong>
      This is REGARA AI-drafted CSR decision-support, not legal or regulatory advice.
      Regulatory decisions rest with the relevant authority. Review every section with
      a qualified regulatory professional before relying on it.
    </footer>
  );
}

function friendlyErrorMessage(raw: string | null): string {
  const msg = (raw ?? '').toString();
  if (!msg) {
    return 'The CSR generation did not complete. Try running it again from the product list.';
  }
  if (/no JSON|did not produce/i.test(msg)) {
    return 'The agent did not produce a valid CSR. Try starting a new run.';
  }
  if (/timeout|timed out/i.test(msg)) {
    return 'The CSR generation took too long to complete. Try starting a new run.';
  }
  if (/auth|api[_ ]?key|unauthor/i.test(msg)) {
    return 'The agent could not reach the report service. Please contact support.';
  }
  return 'Something went wrong while generating the CSR. Try starting a new run, or contact support if this keeps happening.';
}
